import { Listbox } from '@headlessui/react';
import classNames from 'classnames';
import { useField } from 'formik';
import { useRouter } from 'next/router';
import { Fragment } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

type OptionsValues = 'CITIZENSHIP_CARD' | 'RESIDENCE_PERMIT' | 'EMPRESA';

const optionsES = {
  CITIZENSHIP_CARD: 'Cédula de ciudadanía',
  RESIDENCE_PERMIT: 'Cédula de extranjería',
  EMPRESA: 'NIT',
};

const optionsEN = {
  CITIZENSHIP_CARD: 'Citizenship card',
  RESIDENCE_PERMIT: 'Residence permit',
  EMPRESA: 'NIT',
};

const IdTypeSelect = ({ name, flat = false }: { name: string; flat?: boolean }) => {
  const [field, , helper] = useField({ name });
  const router = useRouter();
  const language = router.locale as SupportedLanguages;
  const options = language === 'es' ? optionsES : optionsEN;

  // Variante FLAT (modal "Obtén tu entrada"): MISMO sistema visual que BillingInputField
  // flat (h-44, rounded-12, px-4, bg-white, border-gray-200/70, text-15, foco naranja con
  // ring, container pb-[10px]). El label lo pone el caller ARRIBA → aquí no se repite el
  // label interno (evita el doble "Tipo de documento" / "Tipo de identificación").
  if (flat) {
    return (
      <div className="relative w-full pb-[10px]">
        <Listbox
          value={field.value}
          name={name}
          onChange={(value) => helper.setValue(value)}
        >
          {({ open }) => (
            <>
              <Listbox.Button className="booking-field-fill w-full h-[44px] rounded-[12px] px-4 border border-gray-200/70 hover:border-gray-300 text-[15px] text-gray-900 text-left outline-none transition-all focus:border-tikipal-orange focus:ring-2 focus:ring-tikipal-orange/15 flex items-center justify-between appearance-none">
                <span className="truncate min-w-0">
                  {options[field.value as OptionsValues]}
                </span>
                {open ? (
                  <FiChevronUp className="text-gray-400 shrink-0 ml-2" />
                ) : (
                  <FiChevronDown className="text-gray-400 shrink-0 ml-2" />
                )}
              </Listbox.Button>
              <Listbox.Options className="absolute top-[48px] left-0 z-20 w-full max-h-60 overflow-auto rounded-[12px] border border-gray-200/70 bg-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] focus:outline-none">
                {Object.entries(options).map(([value, label]) => (
                  <Listbox.Option key={value} value={value} as={Fragment}>
                    {({ active }) => (
                      <li
                        className={classNames(
                          'h-11 px-4 flex items-center cursor-pointer text-[15px] text-gray-900',
                          active ? 'bg-tikipal-orange/10' : 'bg-white'
                        )}
                      >
                        {label}
                      </li>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </>
          )}
        </Listbox>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-1 pb-6 pt-1 w-full">
      <label
        className={classNames(
          'absolute',
          '-top-1',
          'left-4',
          'bg-white',
          'px-1',
          'text-philippine-silver',
          'z-10',
          'text-sm'
          // {
          //   "text-copper": focused,
          //   "text-philippine-silver": !focused,
          // }
        )}
      >
        Tipo de identificación
      </label>
      <div className="relative w-full">
        <Listbox
          value={field.value}
          name={name}
          onChange={(value) => {
            helper.setValue(value);
          }}
        >
          {({ open }) => (
            <>
              <Listbox.Button className="appearance-none border text-base rounded-xl h-12 border-philippine-silver px-5 grow focus:border-copper flex justify-between items-center w-full outline-none">
                <div>{options[field.value as OptionsValues]}</div>
                {open ? (
                  <FiChevronUp className="text-philippine-silver" />
                ) : (
                  <FiChevronDown className="text-philippine-silver" />
                )}
              </Listbox.Button>
              <Listbox.Options className="max-h-60 w-full overflow-auto rounded-xl bg-white text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm absolute top-14 left-0 z-20">
                {Object.entries(options).map(([value, label]) => (
                  <Listbox.Option key={value} value={value} as={Fragment}>
                    {({ active, selected }) => (
                      <li
                        className={classNames(
                          'h-14',
                          'px-5',
                          'flex',
                          'items-center',
                          'cursor-pointer',
                          {
                            'bg-copper bg-opacity-25 text-white': active,
                            'bg-white text-black': !active,
                          }
                        )}
                      >
                        {label}
                      </li>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </>
          )}
        </Listbox>
      </div>
    </div>
  );
};

export default IdTypeSelect;
